import { Flex } from '@chakra-ui/react';
import {
  FormFieldAbstract,
  FormFieldAcceleratedPublication,
  FormFieldArticleSubtype,
  FormFieldArticleType,
  FormFieldIPEMMembership,
  FormFieldNoveltyStatement,
  FormFieldRichTextInput,
  FormFieldTextArea,
  FormFieldTextInput,
  FormFieldTitle
} from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import { TextDisplay } from './TextDisplay';
import { TextEmptyState } from './TextEmptyState';
import TextUpdateModal from './TextUpdateModal';
import { FieldHeader } from '../common/FieldHeader';
import { TextDiffModal } from './TextDiffModal';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

// TODO co-locate with other form field types.
// TODO specify union of all known text component types?
export type IFormFieldText = (
  | FormFieldAbstract
  | FormFieldTitle
  | FormFieldNoveltyStatement
  | FormFieldAcceleratedPublication
  | FormFieldIPEMMembership
  | FormFieldTextInput
  | FormFieldTextArea
  | FormFieldArticleType
  | FormFieldArticleSubtype
  | FormFieldRichTextInput
  
) & {
  params: { externalFieldDependency?: { path: string } };
};

type IProps = {
  formFieldDefinition: IFormFieldText;
  value: string;
  previousValue?: string | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: string) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const GenericTextField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated, getExternalDependency } =
    useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const externalDependency = getExternalDependency(
    formFieldDefinition.params.externalFieldDependency?.path
  );

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <TextUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: string) => {
            setIsEditing(false);
            onChange(value);
          }}
          externalDependency={externalDependency}
        />
      )}

      {isUpdated && showDiffModal ? (
        <TextDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? (
        <TextDisplay
          textStyle={
            formFieldDefinition.name === 'Title' ? 'headingTitle' : 'textBody'
          }
          value={value}
        />
      ) : (
        <TextEmptyState />
      )}
    </Flex>
  );
};
